import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Input,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  PiArrowLeftBold,
  PiGlobeDuotone,
  PiInfoDuotone,
  PiListDuotone,
  PiLockDuotone,
  PiChatCircleDotsDuotone,
  PiGearDuotone,
  PiUsersThreeDuotone,
  PiUserPlusDuotone,
  PiQrCodeDuotone,
  PiCopyDuotone,
  PiCalendarBlankDuotone,
  PiPencilSimpleLineDuotone,
  PiBroadcastDuotone,
  PiClockDuotone,
  PiCheckCircleDuotone,
  PiNotePencilDuotone,
  PiMagnifyingGlassDuotone,
  PiCaretRightBold,
  PiScalesDuotone,
  PiXBold
} from "react-icons/pi";
import { QRCodeSVG } from "qrcode.react";
import {
  getGroupWall,
  getTopics,
  TopicSummary,
  WallHeader,
  WallPost,
  WallVoting
} from "@lubbezposrednio-frontend/core";
import { OrganizerPage } from "../OrganizerDashboardView/components/OrganizerShell";
import PeoplePanel from "./PeoplePanel";
import TopicsPanel from "./TopicsPanel";
import GroupSettingsPanel from "./GroupSettingsPanel";
import WallPostCard from "./WallPostCard";
import { CreatePostModal } from "./CreatePostModal";
import { mediaUrl, relTime } from "./wallShared";
import { GLASS, SCROLL_FOG, drgba, EASE } from "../../components/glassUi";
import { SkelLine, SkelBox, SkelCircle } from "../../components/skeletons";

// Re-eksport dla zgodności (część widoków importowała stąd).
export { mediaUrl, relTime };

export const whoCanPostLabel: Record<WallHeader["who_can_post"], string> = {
  all: "Każdy członek może publikować",
  verified: "Tylko zweryfikowani publikują",
  owner: "Tylko organizator i moderatorzy publikują"
};

const whoCanCreateVotingsLabel: Record<NonNullable<WallHeader["who_can_create_votings"]>, string> = {
  owner: "Właściciel",
  moderators: "Moderatorzy",
  members: "Członkowie"
};

const statusMeta: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  ongoing: { label: "Trwa", color: "#1f9e18", icon: PiBroadcastDuotone },
  planned: { label: "Zaplanowane", color: "#7b2ff7", icon: PiClockDuotone },
  finished: { label: "Zakończone", color: "#94a3b8", icon: PiCheckCircleDuotone },
  draft: { label: "Szkic", color: "#b45309", icon: PiNotePencilDuotone }
};

// „Nasza" karta głosowania grupy — ikona statusu + tytuł + badge + termin (nie jednoliniowiec).
const GroupVotingCard: React.FC<{ v: WallVoting }> = ({ v }) => {
  const s = statusMeta[v.status] || { label: v.status, color: "#94a3b8", icon: PiScalesDuotone };
  const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString("pl-PL", { day: "2-digit", month: "short", year: "numeric" }) : null);
  const dates = [fmt(v.start_date), fmt(v.end_date)].filter(Boolean).join(" – ");
  return (
    <Flex as={Link} to={`/glosowanie/${v.id}`} align="center" gap="14px" p="15px 16px" borderRadius="18px" sx={GLASS}
      transition={`all ${EASE}`} _hover={{ textDecoration: "none", transform: "translateY(-2px)", boxShadow: "0 18px 40px -24px rgba(43,0,90,0.5)", borderColor: drgba(s.color, 0.4) }}>
      <Flex w="46px" h="46px" borderRadius="14px" align="center" justify="center" bg={drgba(s.color, 0.12)} flexShrink={0}>
        <Icon as={s.icon} boxSize="24px" color={s.color} />
      </Flex>
      <Box flex="1" minW={0}>
        <Text fontSize="15px" fontWeight="800" color="#241536" noOfLines={1} letterSpacing="-0.01em">{v.title}</Text>
        <HStack spacing="8px" mt="4px" color="#8a8397" fontSize="12.5px" wrap="wrap">
          <HStack spacing="5px" bg={drgba(s.color, 0.1)} color={s.color} px="8px" py="2px" borderRadius="full" fontWeight="800"><Icon as={s.icon} boxSize="12px" /><Text>{s.label}</Text></HStack>
          {dates && <HStack spacing="5px"><Icon as={PiCalendarBlankDuotone} boxSize="13px" /><Text fontWeight="600">{dates}</Text></HStack>}
        </HStack>
      </Box>
      <Icon as={PiCaretRightBold} boxSize="14px" color="#c0b9d0" flexShrink={0} />
    </Flex>
  );
};

// Szkielet karty postu — zgodny z nowym układem (glass, pojedyncza kolumna).
const PostCardSkeleton: React.FC = () => (
  <Box borderRadius="18px" p="16px 18px" sx={GLASS}>
    <HStack spacing="11px" mb="14px">
      <SkelCircle size="40px" />
      <Box flex="1"><SkelLine w="38%" h="13px" /><SkelLine w="22%" h="11px" mt="7px" /></Box>
    </HStack>
    <SkelLine w="92%" h="12px" />
    <SkelLine w="78%" h="12px" mt="8px" />
    <Box mt="14px"><SkelBox h="40px" r="12px" /></Box>
  </Box>
);

// Pigułka filtra statusu z licznikiem.
const FilterChip: React.FC<{ label: string; count: number; active: boolean; onClick: () => void; icon?: React.ElementType; color?: string }> = ({ label, count, active, onClick, icon, color }) => {
  const c = color || "#430098";
  return (
    <Button onClick={onClick} size="sm" borderRadius="full" fontWeight="800" px="13px" flexShrink={0} boxShadow="none"
      leftIcon={icon ? <Icon as={icon} boxSize="14px" /> : undefined}
      color={active ? c : "#6b6480"} bg={active ? drgba(c, 0.12) : "transparent"}
      _hover={{ bg: active ? drgba(c, 0.18) : "rgba(24,12,48,0.06)" }}>
      {label}
      <Box as="span" ml="7px" fontSize="11px" fontWeight="800" color={active ? c : "#a29bb2"}>{count}</Box>
    </Button>
  );
};

// ── Karty paska bocznego ──
// Nagłówek sekcji railu — spójny z pulpitem (ikona kolorowa + tytuł #5a5468 uppercase + opcjonalne „Wszystkie").
const RailHeading: React.FC<{ icon: React.ElementType; title: string; color?: string; onAll?: () => void }> = ({ icon, title, color = "#430098", onAll }) => (
  <Flex justify="space-between" align="center" mb="11px" gap="8px">
    <HStack spacing="8px" minW={0}>
      <Icon as={icon} boxSize="18px" color={color} flexShrink={0} />
      <Text fontSize="12px" fontWeight="800" color="#5a5468" textTransform="uppercase" letterSpacing="0.08em" noOfLines={1}>{title}</Text>
    </HStack>
    {onAll && <Text as="button" onClick={onAll} fontSize="12px" fontWeight="700" color={color} flexShrink={0} _hover={{ textDecoration: "underline" }}>Wszystkie</Text>}
  </Flex>
);
const RailDivider = () => <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />;
const MetaRow: React.FC<{ icon: React.ElementType; color: string; label: string; value: string }> = ({ icon, color, label, value }) => (
  <HStack justify="space-between" spacing="10px">
    <HStack spacing="8px" color="#6b6480" minW={0}><Icon as={icon} boxSize="14px" color={color} flexShrink={0} /><Text fontSize="13px" noOfLines={1}>{label}</Text></HStack>
    <Text fontSize="13px" fontWeight="800" color="#241536" textAlign="right" noOfLines={1} flexShrink={0}>{value}</Text>
  </HStack>
);

const VotingRow: React.FC<{ v: WallVoting }> = ({ v }) => {
  const s = statusMeta[v.status] || { label: v.status, color: "#94a3b8" };
  return (
    <HStack as={Link} to={`/glosowanie/${v.id}`} justify="space-between" py="7px" px="8px" borderRadius="11px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
      <HStack spacing="9px" minW={0}>
        <Box w="7px" h="7px" borderRadius="full" bg={s.color} flexShrink={0} />
        <Text fontSize="13px" fontWeight="600" color="#2f2a3d" noOfLines={1}>{v.title}</Text>
      </HStack>
      <Text fontSize="11px" fontWeight="800" color={s.color} flexShrink={0}>{s.label}</Text>
    </HStack>
  );
};

type Tab = "discussion" | "topics" | "people" | "votings" | "info" | "settings";

const GroupHubView: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const composer = useDisclosure();

  const [header, setHeader] = useState<WallHeader | null>(null);
  const [posts, setPosts] = useState<WallPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [denied, setDenied] = useState(false);
  const [searchParams] = useSearchParams();
  const initialTab = (["discussion", "topics", "people", "votings", "info", "settings"] as const).find(t => t === searchParams.get("tab")) || "discussion";
  const initialPeopleFilter = searchParams.get("filter") || undefined;
  const [tab, setTab] = useState<Tab>(initialTab);
  const [recentTopics, setRecentTopics] = useState<TopicSummary[]>([]);
  const [votingQuery, setVotingQuery] = useState("");
  const [votingStatus, setVotingStatus] = useState<string | null>(null);
  const [pendingTopic, setPendingTopic] = useState<string | null>(null);
  const openTopic = (tid: string) => { setPendingTopic(tid); setTab("topics"); };

  // Sticky, kurczący się nagłówek — po scrollu zwija się do slim-baru, żeby nie przeszkadzać.
  const [condensed, setCondensed] = useState(false);
  useEffect(() => {
    const onScroll = () => setCondensed(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!id) return;
    getTopics(id)
      .then(d => setRecentTopics(d.categories.flatMap(c => c.topics).sort((a, b) => b.last_activity.localeCompare(a.last_activity)).slice(0, 5)))
      .catch(() => undefined);
  }, [id]);

  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [includeTopics, setIncludeTopics] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);

  // mode "full" — pierwsze wczytanie/zmiana grupy (spinner całej strony);
  // "posts" — odświeżenie samej listy (np. filtr Tematy) bez ruszania nagłówka/tabów.
  const load = (mode: "full" | "posts" = "full") => {
    if (mode === "full") setLoading(true); else setPostsLoading(true);
    getGroupWall(id, null, includeTopics)
      .then(r => {
        if (r.data.group) setHeader(r.data.group);
        setPosts(r.data.posts ?? []);
        setCursor(r.data.next_cursor ?? null);
        setHasMore(!!r.data.has_more);
      })
      .catch(err => {
        if (err?.response?.status === 403) setDenied(true);
      })
      .finally(() => { if (mode === "full") setLoading(false); else setPostsLoading(false); });
  };

  const loadMore = () => {
    if (loadingMoreRef.current || !cursor) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    getGroupWall(id, cursor, includeTopics)
      .then(r => {
        setPosts(prev => {
          const seen = new Set(prev.map(p => p.id));
          return [...prev, ...(r.data.posts ?? []).filter(p => !seen.has(p.id))];
        });
        setCursor(r.data.next_cursor ?? null);
        setHasMore(!!r.data.has_more);
      })
      .catch(() => setHasMore(false))
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    document.title = "Grupa";
    load("full");
    // eslint-disable-next-line
  }, [id]);

  // Zmiana filtra „Tematy" odświeża tylko listę postów (bez przeładowania całej strony).
  const topicsFirstRef = useRef(true);
  useEffect(() => {
    if (topicsFirstRef.current) { topicsFirstRef.current = false; return; }
    load("posts");
    // eslint-disable-next-line
  }, [includeTopics]);

  useEffect(() => {
    if (tab !== "discussion" || !hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) loadMore();
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line
  }, [tab, hasMore, cursor]);

  const updatePost = (postId: string, updater: (p: WallPost) => WallPost) =>
    setPosts(prev => prev.map(p => (p.id === postId ? updater(p) : p)).sort((a, b) => Number(b.pinned) - Number(a.pinned)));

  if (loading) {
    return (
      <Flex align="flex-start">
        <Box flex="1" minW={0}>
          <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} py={{ base: "24px", md: "36px" }}>
            {/* Hero */}
            <Box borderRadius="24px" mb="16px" p={{ base: "18px", md: "22px" }} sx={GLASS}>
              <HStack spacing="16px" align="center">
                <SkelCircle size="80px" />
                <Box flex="1"><SkelLine w="45%" h="24px" /><SkelLine w="30%" h="13px" mt="10px" /></Box>
              </HStack>
              <HStack spacing="8px" mt="16px" pt="14px" borderTop="1px solid rgba(24,12,48,0.07)">
                {Array.from({ length: 5 }).map((_, i) => <SkelLine key={i} w="88px" h="30px" r="full" />)}
              </HStack>
            </Box>
            {/* Composer + posty */}
            <VStack align="stretch" spacing="16px">
              <Box borderRadius="18px" p="14px 16px" sx={GLASS}><HStack spacing="11px"><SkelCircle size="40px" /><SkelBox h="42px" r="full" /></HStack></Box>
              {Array.from({ length: 3 }).map((_, i) => <PostCardSkeleton key={i} />)}
            </VStack>
          </Box>
        </Box>
        {/* Rail */}
        <Box as="aside" display={{ base: "none", xl: "block" }} w="384px" flexShrink={0} alignSelf="stretch">
          <Box position="sticky" top="0" h="100vh" p="12px">
            <Box h="100%" borderRadius="26px" p="20px" sx={GLASS}>
              <HStack spacing="12px" mb="18px"><SkelCircle size="48px" /><Box flex="1"><SkelLine w="60%" h="15px" /><SkelLine w="40%" h="11px" mt="8px" /></Box></HStack>
              {Array.from({ length: 4 }).map((_, i) => (
                <Box key={i} mb="18px"><SkelLine w="35%" h="11px" mb="12px" /><SkelBox h="52px" r="12px" /></Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Flex>
    );
  }

  if (denied || !header) {
    return (
      <OrganizerPage>
        <HStack as={Link} to="/organizator/moje-grupy" spacing="8px" color="#6b6480" fontSize="14px" fontWeight="700" mb="18px" _hover={{ color: "primary" }} w="fit-content">
          <Icon as={PiArrowLeftBold} boxSize="14px" />
          <Text>Moje grupy</Text>
        </HStack>
        <Flex direction="column" align="center" justify="center" py="70px" borderRadius="22px" sx={GLASS}>
          <Flex w="58px" h="58px" borderRadius="16px" bg={drgba("#430098", 0.1)} align="center" justify="center" mb="14px">
            <Icon as={PiLockDuotone} boxSize="28px" color="primary" />
          </Flex>
          <Text color="#241536" fontWeight="900" mb="4px" fontSize="18px" letterSpacing="-0.01em">Brak dostępu do grupy</Text>
          <Text color="#6b6480" fontSize="14px" textAlign="center" maxW="360px">Ta grupa jest widoczna tylko dla swoich członków.</Text>
        </Flex>
      </OrganizerPage>
    );
  }

  const manager = header.is_owner || header.is_moderator;
  const TABS: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: "discussion", label: "Aktualności", icon: PiChatCircleDotsDuotone },
    { key: "topics", label: "Tematy", icon: PiListDuotone },
    { key: "people", label: "Osoby", icon: PiUsersThreeDuotone },
    { key: "votings", label: "Głosowania", icon: PiGlobeDuotone },
    { key: "info", label: "Informacje", icon: PiInfoDuotone },
    ...(header.is_owner ? [{ key: "settings" as Tab, label: "Ustawienia", icon: PiGearDuotone }] : [])
  ];

  const signupLink = header.signup_token ? `${window.location.origin}/zapisy/${header.signup_token}` : "";
  const copySignup = () => { if (!signupLink) return; navigator.clipboard?.writeText(signupLink); toast({ title: "Skopiowano link zapisów", status: "success", duration: 1600, isClosable: true }); };

  const postLabel = header.who_can_post === "all" ? "Wszyscy" : header.who_can_post === "verified" ? "Zweryfikowani" : "Właściciel";
  const createLabel = header.who_can_create_votings === "members" ? "Członkowie" : header.who_can_create_votings === "moderators" ? "Moderatorzy" : "Właściciel";

  // ── Główny prawy rail — styl pulpitu: jeden panel, sekcje dzielone hairline + RailHeading ──
  const rail = (
    <Box>
      {/* Tożsamość grupy */}
      <Flex direction="column" align="center" textAlign="center" gap="11px">
        <Avatar boxSize="66px" name={header.name} src={mediaUrl(header.avatar_url)} bg={drgba("#430098", 0.12)} color="primary" borderColor="white" borderWidth="3px" icon={<Icon as={PiUsersThreeDuotone} boxSize="30px" />} />
        <Box>
          <Text fontSize="17px" fontWeight="900" color="#1b1030" noOfLines={2} letterSpacing="-0.01em" lineHeight="1.2">{header.name}</Text>
          <HStack justify="center" spacing="7px" mt="5px" color="#8a8397" fontSize="12.5px" wrap="wrap">
            <HStack spacing="4px"><Icon as={header.visibility === "public" ? PiGlobeDuotone : PiLockDuotone} boxSize="12px" /><Text fontWeight="700">{header.visibility === "public" ? "Publiczna" : "Prywatna"}</Text></HStack>
            <Text>·</Text>
            <Text fontWeight="700">{header.members_count} {header.members_count === 1 ? "członek" : "członków"}</Text>
          </HStack>
        </Box>
        {header.is_owner && (
          <Button size="sm" onClick={() => setTab("settings")} leftIcon={<Icon as={PiGearDuotone} boxSize="14px" />} variant="outline" borderColor="rgba(67,0,152,0.22)" color="primary" borderRadius="11px" fontWeight="800" _hover={{ bg: drgba("#430098", 0.05) }}>Zarządzaj</Button>
        )}
      </Flex>

      <RailDivider />
      <RailHeading icon={PiInfoDuotone} title="O grupie" color="#2563eb" />
      <Text color="#4a4458" fontSize="14px" whiteSpace="pre-wrap" noOfLines={6} mb="14px" lineHeight="1.55">{header.description || "Brak opisu grupy."}</Text>
      <VStack align="stretch" spacing="10px">
        <MetaRow icon={PiChatCircleDotsDuotone} color="#7b2ff7" label="Kto publikuje" value={postLabel} />
        <MetaRow icon={PiGlobeDuotone} color="#e51f44" label="Kto tworzy głosowania" value={createLabel} />
        {header.created_at && <MetaRow icon={PiCalendarBlankDuotone} color="#0d9488" label="Utworzona" value={new Date(header.created_at).toLocaleDateString("pl-PL")} />}
      </VStack>

      <RailDivider />
      <RailHeading icon={PiUsersThreeDuotone} title="Członkowie" color="#7b2ff7" onAll={() => setTab("people")} />
      {header.members_preview.length === 0 ? (
        <Text color="#a8a2b6" fontSize="13px">Brak członków.</Text>
      ) : (
        <Flex align="center" justify="space-between" gap="10px">
          <AvatarGroup size="sm" max={5} spacing="-10px">
            {header.members_preview.slice(0, 8).map((m, i) => <Avatar key={i} name={m.name} src={mediaUrl(m.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" borderColor="white" />)}
          </AvatarGroup>
          <Box px="11px" py="4px" borderRadius="full" bg={drgba("#7b2ff7", 0.1)} flexShrink={0}><Text fontSize="12.5px" fontWeight="800" color="#7b2ff7" whiteSpace="nowrap">{header.members_count}</Text></Box>
        </Flex>
      )}

      <RailDivider />
      <RailHeading icon={PiListDuotone} title="Dyskusje" color="#7b2ff7" onAll={() => setTab("topics")} />
      {recentTopics.length === 0 ? (
        <Text color="#a8a2b6" fontSize="13px">Brak tematów.</Text>
      ) : (
        <VStack align="stretch" spacing="10px">
          {recentTopics.map(t => (
            <Box key={t.id} as="button" textAlign="left" onClick={() => openTopic(t.id)} _hover={{ "& .tt": { color: "#430098" } }}>
              <HStack spacing="7px"><Icon as={PiListDuotone} boxSize="13px" color="#a8a2b6" flexShrink={0} /><Text className="tt" fontSize="13.5px" fontWeight="700" color="#2f2a3d" noOfLines={1} transition={`color ${EASE}`}>{t.title}</Text></HStack>
              <Text fontSize="11px" color="#a8a2b6" pl="20px">{t.posts_count} {t.posts_count === 1 ? "post" : "postów"} · {relTime(t.last_activity)}</Text>
            </Box>
          ))}
        </VStack>
      )}

      <RailDivider />
      <RailHeading icon={PiGlobeDuotone} title="Głosowania grupy" color="#e51f44" onAll={header.votings.length > 0 ? () => setTab("votings") : undefined} />
      {header.votings.length === 0 ? (
        <Text color="#a8a2b6" fontSize="13px">Brak powiązanych głosowań.</Text>
      ) : (
        <VStack align="stretch" spacing="1px">{header.votings.slice(0, 5).map(v => <VotingRow key={v.id} v={v} />)}</VStack>
      )}
      {header.can_create_voting && (
        <Button as={Link} to={`/organizator/utworz-glosowanie?grupa=${header.id}`} mt="12px" w="100%" size="sm" leftIcon={<Icon as={PiGlobeDuotone} boxSize="14px" />} bg={drgba("#e51f44", 0.1)} color="#e51f44" borderRadius="11px" fontWeight="800" _hover={{ bg: drgba("#e51f44", 0.18) }}>Utwórz głosowanie</Button>
      )}

      {header.signup_enabled && signupLink && (
        <>
          <RailDivider />
          <RailHeading icon={PiUserPlusDuotone} title="Zapisy do grupy" color="#10b981" />
          <Flex justify="center" mb="12px"><Box p="10px" bg="white" borderRadius="14px" boxShadow="0 10px 30px -16px rgba(43,0,90,0.4)"><QRCodeSVG value={signupLink} size={130} fgColor="#1b1030" bgColor="#ffffff" level="M" /></Box></Flex>
          <HStack spacing="6px" bg="rgba(24,12,48,0.04)" borderRadius="10px" pl="12px" pr="5px" py="5px">
            <Text flex="1" fontSize="12px" fontWeight="600" color="#4a4458" noOfLines={1}>{signupLink}</Text>
            <Button size="xs" h="30px" onClick={copySignup} leftIcon={<Icon as={PiCopyDuotone} boxSize="13px" />} bg="primary" color="white" borderRadius="8px" fontWeight="800" fontSize="12px" _hover={{ bg: "#36007a" }} flexShrink={0}>Kopiuj</Button>
          </HStack>
          {header.signup_closes_at && <HStack spacing="6px" mt="10px" color="#8a8397"><Icon as={PiCalendarBlankDuotone} boxSize="13px" /><Text fontSize="12px" fontWeight="700">Zapisy do {new Date(header.signup_closes_at).toLocaleDateString("pl-PL")}</Text></HStack>}
        </>
      )}
    </Box>
  );

  return (
    <Flex align="flex-start">
      {/* Kompaktowy pasek — wjeżdża z góry przy scrollu (osobny overlay, nie rusza układu) */}
      <Box
        position="fixed"
        top={{ base: "64px", lg: "0" }}
        left={{ base: 0, lg: "288px" }}
        right={{ base: 0, xl: "384px" }}
        zIndex={40}
        pointerEvents={condensed ? "auto" : "none"}
        opacity={condensed ? 1 : 0}
        transform={condensed ? "translateY(0)" : "translateY(-100%)"}
        transition={`transform ${EASE}, opacity ${EASE}`}
      >
        <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} pt="10px">
          <Flex align="center" justify="space-between" gap="12px" borderRadius="16px" px={{ base: "14px", md: "16px" }} py="10px" sx={GLASS} boxShadow="0 16px 36px -22px rgba(43,0,90,0.55)">
            <HStack minW={0} spacing="11px">
              <Avatar boxSize="34px" name={header.name} src={mediaUrl(header.avatar_url)} bg={drgba("#430098", 0.12)} color="primary" icon={<Icon as={PiUsersThreeDuotone} boxSize="17px" />} flexShrink={0} />
              <Text minW={0} noOfLines={1} fontSize={{ base: "16px", md: "18px" }} fontWeight="900" color="#1b1030" letterSpacing="-0.01em">{header.name}</Text>
            </HStack>
            {header.can_post && (
              <Button onClick={() => { setTab("discussion"); composer.onOpen(); }} leftIcon={<Icon as={PiPencilSimpleLineDuotone} />} size="sm" bg="primary" color="white" borderRadius="11px" fontWeight="800" flexShrink={0} _hover={{ bg: "#360079" }} _active={{ bg: "#2c0063" }}>
                Napisz post
              </Button>
            )}
          </Flex>
        </Box>
      </Box>

      <Box flex="1" minW={0}>
        <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} py={{ base: "24px", md: "36px" }}>
      {/* HERO — normalny (scrolluje się); kompaktowy pasek wjeżdża osobno z góry przy scrollu */}
      <Box borderRadius="24px" mb="16px" sx={GLASS}>
        <Box px={{ base: "18px", md: "26px" }} pt={{ base: "18px", md: "22px" }} pb="16px">
          <Flex align={{ base: "flex-start", sm: "center" }} gap="16px" direction={{ base: "column", sm: "row" }}>
            <Avatar
              boxSize={{ base: "70px", md: "80px" }}
              name={header.name}
              src={mediaUrl(header.avatar_url)}
              bg={drgba("#430098", 0.12)}
              color="primary"
              icon={<Icon as={PiUsersThreeDuotone} boxSize="32px" />}
              flexShrink={0}
            />
            <Flex flex="1" minW={0} justify="space-between" align={{ base: "flex-start", md: "flex-end" }} direction={{ base: "column", md: "row" }} gap="12px" w="100%" pb="4px">
              <Box minW={0}>
                <Text fontSize={{ base: "23px", md: "27px" }} fontWeight="900" color="#1b1030" noOfLines={1} letterSpacing="-0.01em">{header.name}</Text>
                <HStack spacing="9px" color="#6b6480" fontSize="13.5px" mt="3px" wrap="wrap">
                  <HStack spacing="5px">
                    <Icon as={header.visibility === "public" ? PiGlobeDuotone : PiLockDuotone} boxSize="14px" />
                    <Text fontWeight="700">{header.visibility === "public" ? "Grupa publiczna" : "Grupa prywatna"}</Text>
                  </HStack>
                  <Text>·</Text>
                  <Text fontWeight="700">{header.members_count} {header.members_count === 1 ? "członek" : "członków"}</Text>
                </HStack>
              </Box>
              <HStack spacing="8px" flexShrink={0}>
                {header.members_preview.length > 0 && (
                  <AvatarGroup size="sm" max={5} spacing="-10px">
                    {header.members_preview.map((m, i) => (
                      <Avatar key={i} name={m.name} src={mediaUrl(m.avatar_url)} bg={drgba("#430098", 0.12)} color="primary" />
                    ))}
                  </AvatarGroup>
                )}
                {header.is_owner && (
                  <Button onClick={() => setTab("settings")} leftIcon={<Icon as={PiGearDuotone} />} size="sm" variant="outline" borderColor="rgba(67,0,152,0.25)" color="primary" borderRadius="11px" fontWeight="800" _hover={{ bg: drgba("#430098", 0.05) }}>
                    Zarządzaj
                  </Button>
                )}
              </HStack>
            </Flex>
          </Flex>

          {/* SUBNAV */}
          <HStack spacing="4px" borderTop="1px solid rgba(24,12,48,0.07)" mt="14px" pt="12px" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
            {TABS.map(t => {
              const active = tab === t.key;
              return (
                <Button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  leftIcon={<Icon as={t.icon} boxSize="15px" />}
                  size="sm"
                  borderRadius="full"
                  fontWeight="800"
                  px="15px"
                  color={active ? "#7b2ff7" : "#6b6480"}
                  bg={active ? "rgba(123,47,247,0.1)" : "transparent"}
                  boxShadow="none"
                  _hover={{ bg: active ? "rgba(123,47,247,0.16)" : "rgba(24,12,48,0.06)" }}
                  flexShrink={0}
                >
                  {t.label}
                </Button>
              );
            })}
          </HStack>
        </Box>
      </Box>

      {/* BODY — pojedyncza kolumna; meta w prawym railu */}
      <Box mt="16px">
          {tab === "discussion" && (
            <VStack align="stretch" spacing="16px">
              {/* Composer trigger */}
              {header.can_post ? (
                <Box borderRadius="18px" p="14px 16px" sx={GLASS}>
                  <HStack spacing="11px">
                    <Avatar size="sm" boxSize="40px" name={header.me.name} src={mediaUrl(header.me.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" />
                    <Box as="button" onClick={composer.onOpen} flex="1" textAlign="left" bg="rgba(255,255,255,0.6)" border="1px solid rgba(24,12,48,0.06)" _hover={{ bg: drgba("#430098", 0.06) }} borderRadius="full" px="18px" py="11px" transition={`background ${EASE}`}>
                      <Text color="#8a8397" fontSize="15px">Co słychać w grupie?</Text>
                    </Box>
                  </HStack>
                  <HStack spacing="0" mt="10px" pt="10px" borderTop="1px solid rgba(24,12,48,0.06)">
                    <Button onClick={composer.onOpen} flex="1" variant="ghost" leftIcon={<Icon as={PiChatCircleDotsDuotone} color="#7b2ff7" />} color="#5a5468" fontWeight="800" size="sm" _hover={{ bg: drgba("#430098", 0.05) }}>
                      Napisz post
                    </Button>
                    {header.can_create_voting && (
                      <Button as={Link} to={`/organizator/utworz-glosowanie?grupa=${header.id}`} flex="1" variant="ghost" leftIcon={<Icon as={PiGlobeDuotone} color="#e51f44" />} color="#5a5468" fontWeight="800" size="sm" _hover={{ bg: "rgba(229,31,68,0.05)" }}>
                        Utwórz głosowanie
                      </Button>
                    )}
                  </HStack>
                </Box>
              ) : (
                <Flex bg={drgba("#430098", 0.04)} borderRadius="14px" p="12px 16px" align="center" gap="10px">
                  <Icon as={PiLockDuotone} color="#a8a2b6" boxSize="16px" />
                  <Text fontSize="13px" color="#6b6480">{whoCanPostLabel[header.who_can_post]} — nie masz uprawnień do publikacji.</Text>
                </Flex>
              )}

              <Flex align="center" gap="9px" borderRadius="14px" px="14px" py="10px" sx={GLASS}>
                <Switch size="sm" colorScheme="purple" isChecked={includeTopics} onChange={e => setIncludeTopics(e.target.checked)} />
                <Text fontSize="13px" color="#5a5468" fontWeight="700">Pokaż też posty z dyskusji (Tematy)</Text>
              </Flex>

              {postsLoading ? (
                <>{Array.from({ length: 3 }).map((_, i) => <PostCardSkeleton key={i} />)}</>
              ) : posts.length === 0 ? (
                <Flex direction="column" align="center" justify="center" py="48px" borderRadius="20px" sx={GLASS}>
                  <Flex w="52px" h="52px" borderRadius="15px" bg={drgba("#430098", 0.1)} align="center" justify="center" mb="12px">
                    <Icon as={PiChatCircleDotsDuotone} boxSize="24px" color="primary" />
                  </Flex>
                  <Text color="#241536" fontWeight="900" mb="3px">Brak postów</Text>
                  <Text color="#6b6480" fontSize="14px">Bądź pierwszą osobą, która coś tu napisze.</Text>
                </Flex>
              ) : (
                posts.map(p => (
                  <WallPostCard
                    key={p.id}
                    post={p}
                    groupId={id}
                    isModerator={header.is_moderator}
                    onChange={updater => updatePost(p.id, updater)}
                    onRemove={() => setPosts(prev => prev.filter(x => x.id !== p.id))}
                  />
                ))
              )}

              {hasMore && (
                <Flex ref={sentinelRef} justify="center" py="20px">
                  {loadingMore && <Spinner color="primary" thickness="3px" />}
                </Flex>
              )}
            </VStack>
          )}

          {tab === "topics" && <TopicsPanel groupId={id} canManage={manager} header={header} initialTopicId={pendingTopic} />}

          {tab === "people" && <PeoplePanel groupId={id} canManage={manager} initialStatus={initialPeopleFilter} />}

          {tab === "settings" && header.is_owner && (
            <GroupSettingsPanel groupId={id} onDeleted={() => navigate("/organizator/grupy")} onSaved={() => load()} />
          )}

          {tab === "votings" && (() => {
            const all = header.votings;
            // Statusy obecne w tej grupie (do filtrów) + liczby.
            const order = ["ongoing", "planned", "finished", "draft"];
            const statusCounts: Record<string, number> = {};
            all.forEach(v => { statusCounts[v.status] = (statusCounts[v.status] ?? 0) + 1; });
            const presentStatuses = order.filter(s => statusCounts[s]);
            const q = votingQuery.trim().toLowerCase();
            const filtered = all.filter(v => (!votingStatus || v.status === votingStatus) && (!q || v.title.toLowerCase().includes(q)));

            return (
              <VStack align="stretch" spacing="16px">
                {/* Toolbar — nagłówek + wyszukiwarka + filtry (na cardzie, nie na gołym tle) */}
                <Box borderRadius="20px" p={{ base: "16px", md: "18px" }} sx={GLASS}>
                  <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap="12px" direction={{ base: "column", md: "row" }}>
                    <HStack spacing="10px">
                      <Flex w="34px" h="34px" borderRadius="11px" bg={drgba("#e51f44", 0.1)} align="center" justify="center"><Icon as={PiScalesDuotone} boxSize="19px" color="#e51f44" /></Flex>
                      <Box>
                        <Text fontSize="17px" fontWeight="900" color="#241536" letterSpacing="-0.01em">Głosowania grupy</Text>
                        <Text fontSize="12.5px" color="#8a8397" fontWeight="600">{all.length} {all.length === 1 ? "głosowanie" : "głosowań"}</Text>
                      </Box>
                    </HStack>
                    {header.can_create_voting && (
                      <Button as={Link} to={`/organizator/utworz-glosowanie?grupa=${header.id}`} size="sm" leftIcon={<Icon as={PiScalesDuotone} boxSize="15px" />} bg="accent" color="white" borderRadius="11px" fontWeight="800" flexShrink={0} _hover={{ bg: "#c41a3a", textDecoration: "none" }}>Utwórz głosowanie</Button>
                    )}
                  </Flex>

                  {all.length > 0 && (
                    <VStack align="stretch" spacing="11px" mt="14px">
                      {/* Wyszukiwarka */}
                      <HStack spacing="10px" bg="rgba(255,255,255,0.7)" border="1px solid rgba(24,12,48,0.08)" borderRadius="13px" px="14px" h="44px" _focusWithin={{ borderColor: drgba("#430098", 0.4), boxShadow: `0 0 0 3px ${drgba("#430098", 0.12)}` }}>
                        <Icon as={PiMagnifyingGlassDuotone} boxSize="18px" color="#9a94ab" />
                        <Input variant="unstyled" placeholder="Szukaj głosowania…" value={votingQuery} onChange={e => setVotingQuery(e.target.value)} fontSize="14px" fontWeight="600" />
                        {votingQuery && <Icon as={PiXBold} boxSize="13px" color="#9a94ab" cursor="pointer" onClick={() => setVotingQuery("")} />}
                      </HStack>
                      {/* Filtry statusu */}
                      <HStack spacing="7px" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
                        <FilterChip label="Wszystkie" count={all.length} active={!votingStatus} onClick={() => setVotingStatus(null)} />
                        {presentStatuses.map(s => (
                          <FilterChip key={s} label={statusMeta[s].label} icon={statusMeta[s].icon} color={statusMeta[s].color} count={statusCounts[s]} active={votingStatus === s} onClick={() => setVotingStatus(votingStatus === s ? null : s)} />
                        ))}
                      </HStack>
                    </VStack>
                  )}
                </Box>

                {all.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" py="48px" borderRadius="20px" sx={GLASS}>
                    <Flex w="52px" h="52px" borderRadius="15px" bg={drgba("#e51f44", 0.1)} align="center" justify="center" mb="12px"><Icon as={PiScalesDuotone} boxSize="26px" color="#e51f44" /></Flex>
                    <Text fontSize="15px" fontWeight="800" color="#241536">Brak głosowań</Text>
                    <Text fontSize="13.5px" color="#8a8397" mt="3px" textAlign="center" maxW="320px">Ta grupa nie ma jeszcze powiązanych głosowań.</Text>
                    {header.can_create_voting && (
                      <Button as={Link} to={`/organizator/utworz-glosowanie?grupa=${header.id}`} mt="16px" size="sm" bg="accent" color="white" borderRadius="11px" fontWeight="800" _hover={{ bg: "#c41a3a", textDecoration: "none" }}>Utwórz pierwsze</Button>
                    )}
                  </Flex>
                ) : filtered.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" py="40px" borderRadius="20px" sx={GLASS}>
                    <Icon as={PiMagnifyingGlassDuotone} boxSize="30px" color="#c0b9d0" mb="10px" />
                    <Text fontSize="14px" fontWeight="800" color="#241536">Nic nie znaleziono</Text>
                    <Text fontSize="13px" color="#8a8397" mt="3px">Zmień frazę lub filtr statusu.</Text>
                    <Button mt="14px" size="xs" variant="ghost" color="primary" fontWeight="800" onClick={() => { setVotingQuery(""); setVotingStatus(null); }} _hover={{ bg: drgba("#430098", 0.06) }}>Wyczyść filtry</Button>
                  </Flex>
                ) : (
                  <VStack align="stretch" spacing="10px">{filtered.map(v => <GroupVotingCard key={v.id} v={v} />)}</VStack>
                )}
              </VStack>
            );
          })()}

          {tab === "info" && (
            <VStack align="stretch" spacing="16px">
              {/* O grupie — opis */}
              <Box borderRadius="20px" p={{ base: "18px", md: "22px" }} sx={GLASS}>
                <HStack spacing="10px" mb="12px">
                  <Flex w="34px" h="34px" borderRadius="11px" bg={drgba("#7b2ff7", 0.1)} align="center" justify="center"><Icon as={PiInfoDuotone} boxSize="19px" color="#7b2ff7" /></Flex>
                  <Text fontSize="17px" fontWeight="900" color="#241536" letterSpacing="-0.01em">O grupie</Text>
                </HStack>
                {header.description ? (
                  <Text color="#4a4458" fontSize="14.5px" lineHeight="1.6" whiteSpace="pre-wrap">{header.description}</Text>
                ) : (
                  <Text color="#a29bb2" fontSize="14px" fontStyle="italic">Brak opisu.</Text>
                )}
              </Box>

              {/* Fakty — kafelki z duotone ikonami */}
              <Box borderRadius="20px" p={{ base: "18px", md: "22px" }} sx={GLASS}>
                <Text fontSize="12px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#a29bb2" mb="14px">Szczegóły</Text>
                <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap="10px">
                  {[
                    { icon: header.visibility === "public" ? PiGlobeDuotone : PiLockDuotone, color: header.visibility === "public" ? "#2563eb" : "#6b7280", label: "Widoczność", value: header.visibility === "public" ? "Publiczna" : "Prywatna" },
                    { icon: PiChatCircleDotsDuotone, color: "#0d9488", label: "Kto może publikować", value: whoCanPostLabel[header.who_can_post] },
                    ...(header.who_can_create_votings ? [{ icon: PiScalesDuotone, color: "#e51f44", label: "Kto tworzy głosowania", value: whoCanCreateVotingsLabel[header.who_can_create_votings] }] : []),
                    { icon: PiUsersThreeDuotone, color: "#7b2ff7", label: "Członkowie", value: String(header.members_count) },
                    ...(header.created_at ? [{ icon: PiCalendarBlankDuotone, color: "#b45309", label: "Utworzona", value: new Date(header.created_at).toLocaleDateString("pl-PL", { day: "2-digit", month: "long", year: "numeric" }) }] : [])
                  ].map((f, i) => (
                    <HStack key={i} spacing="11px" bg="rgba(24,12,48,0.028)" borderRadius="15px" p="13px 14px" align="center">
                      <Flex w="38px" h="38px" borderRadius="11px" bg={drgba(f.color, 0.12)} align="center" justify="center" flexShrink={0}><Icon as={f.icon} boxSize="19px" color={f.color} /></Flex>
                      <Box minW={0}>
                        <Text fontSize="11.5px" fontWeight="800" letterSpacing="0.03em" textTransform="uppercase" color="#a29bb2" noOfLines={1}>{f.label}</Text>
                        <Text fontSize="14.5px" fontWeight="800" color="#241536" noOfLines={1}>{f.value}</Text>
                      </Box>
                    </HStack>
                  ))}
                </Box>
              </Box>
            </VStack>
          )}
      </Box>

          {/* Rail zwinięty <xl */}
          <Box display={{ base: "block", xl: "none" }} mt="16px" borderRadius="22px" px="18px" py="20px" sx={GLASS}>{rail}</Box>
        </Box>
      </Box>

      {/* ── GŁÓWNY PRAWY RAIL — pływający, pełnowysokościowy (jak w kreatorach) ── */}
      <Box as="aside" display={{ base: "none", xl: "block" }} w="384px" flexShrink={0} alignSelf="stretch">
        <Box position="sticky" top="0" h="100vh" p="12px">
          <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
            <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 20%, rgba(124,58,237,0.14), transparent 72%)" }} />
            <VStack position="relative" align="stretch" spacing="0" h="100%" px="16px" py="20px" sx={{ ...SCROLL_FOG, maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)" }}>
              {rail}
            </VStack>
          </Box>
        </Box>
      </Box>

      <CreatePostModal
        isOpen={composer.isOpen}
        onClose={composer.onClose}
        groupId={id}
        groupName={header.name}
        me={header.me}
        onCreated={p => setPosts(prev => [p, ...prev])}
      />
    </Flex>
  );
};

export default GroupHubView;
